import { Navigate } from 'react-router-dom';
import DashboardLayout from '../shell/layouts/DashboardLayout.jsx';
import { ProtectedRoute } from '../authentication/ProtectedRoute.jsx';
import { RoleProtectedRoute } from '../authentication/RoleProtectedRoute.jsx';
import { PAGES, COMPONENTS } from '../config/pagePaths.generated.js';
import { RequireActiveScopeOutlet } from '../authentication/RequireActiveScope.jsx';
import { lazyWithRetry } from '../utils/lazyWithRetry.js';

const OrgManagement = lazyWithRetry(() => import('../pages/admin/access-management/org-management/OrgManagement'));
const GameRegistration = lazyWithRetry(() => import('../pages/admin/game-management/game-registration/game-registration-homepage/GameRegistration.jsx'));
const GameRegistrationForm = lazyWithRetry(() => import('../pages/admin/game-management/game-registration/game-registration-form/GameRegistrationForm.jsx'));
const RoleManagement = lazyWithRetry(() => import('../pages/admin/role-access-management/role-mgmt/RoleManagement'));
const CombinedUserManagement = lazyWithRetry(() => import('../pages/admin/user-management/CombinedUserManagement'));
const PageManagement = lazyWithRetry(() => import('../pages/admin/role-access-management/page-mgmt/PageManagement'));
const AdminSettingsAccess = lazyWithRetry(() => import('../pages/admin/role-access-management/admin-settings-access/AdminSettingsAccess'));
const DataConnector = lazyWithRetry(() => import('../pages/admin/game-management/data-connector-management/DataConnector.jsx'));
const DataConnectorCreatePage = lazyWithRetry(() => import('../pages/admin/game-management/data-connector-management/DataConnectorCreatePage.jsx'));
const EditTablesPage = lazyWithRetry(() => import('../pages/admin/game-management/data-connector-management/EditTablesPage.jsx'));
// Per-game workspace IA (Option B). Per-game sub-pages live under
// `/games/:gameId/...` and the index route redirects to `/setup`
// (the only sensible default — there's no "overview" landing since
// the tab strip already exposes every destination). Existing
// per-game URLs (data-connections/:gameId, update-game/:gameId)
// stay live as aliases during the transition.
// Eager (not lazyWithRetry): the workspace shell must render immediately so its FormHeader + tab band
// are present from the first frame — the tab chunk then loads inside the shell's own Suspense
// (GameWorkspaceLayout), so the loader never moves and the header never blinks out.
import GameWorkspaceLayout from '../pages/admin/game-management/game-workspace/GameWorkspaceLayout.jsx';
// Direct-entry landing for the workspace nav item. Redirects to the
// pinned game's workspace if one is selected, otherwise renders a
// grid of accessible games — see GameWorkspacePicker.jsx for the
// three-branch (fast-path / single-game / picker) UX.
const GameWorkspacePicker = lazyWithRetry(() => import('../pages/admin/game-management/game-workspace/GameWorkspacePicker.jsx'));
const GameSetup = lazyWithRetry(() => import('../pages/admin/game-management/game-setup/GameSetup.jsx'));
// Game Knowledge — moved here from Live Ops; rendered under the workspace "Knowledge" tab.
const GameKnowledge = lazyWithRetry(() => import('../pages/admin/game-management/game-knowledge/GameKnowledge.jsx'));
const GameKnowledgeNewPage = lazyWithRetry(() => import('../pages/admin/game-management/game-knowledge/GameKnowledgeNewPage.jsx'));
const GameKnowledgeExtractionPage = lazyWithRetry(() => import('../pages/admin/game-management/game-knowledge/GameKnowledgeExtractionPage.jsx'));
const GameKnowledgeDraftsPage = lazyWithRetry(() => import('../pages/admin/game-management/game-knowledge/GameKnowledgeDraftsPage.jsx'));
const GameKnowledgeBulkUploadPage = lazyWithRetry(() => import('../pages/admin/game-management/game-knowledge/GameKnowledgeBulkUploadPage.jsx'));
// Chunk map — read-only view of how one knowledge document was chunked,
// summarised, and embedded into intel's kb_nodes vector store.
const ChunkMapPage = lazyWithRetry(() => import('../pages/admin/game-management/game-knowledge/ChunkMapPage.jsx'));
const GrantAccessWizard = lazyWithRetry(() => import('../pages/admin/access-management/grant-access/GrantAccessWizard.jsx'));

const settingRoutes = [
    {
        path: 'admin-settings',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="settings" />
            </ProtectedRoute>
        ),
        children: [
            {
                path: '',
                element: <Navigate to="role-access-mgmt" replace />,
            },
            {
                path: 'role-access-mgmt',
                children: [
                    {
                        path: '',
                        element: <Navigate to="role" replace />,
                    },
                    {
                        // Role Management → org owners (page permission; others resolve NO_ACCESS).
                        path: 'role',
                        element: <RoleProtectedRoute pagePath={PAGES.ROLE_MGMT}><RoleManagement /></RoleProtectedRoute>,
                    },
                    {
                        // Page/Component Management → super users only (ControlPlaneAccessPolicy on the server).
                        path: 'page',
                        element: <RoleProtectedRoute superUserOnly pagePath={PAGES.PAGE_MGMT}><PageManagement /></RoleProtectedRoute>,
                    },
                    {
                        // Admin Settings Access → super users only: which built-in roles reach the Admin Settings
                        // pages in this environment, on top of rbac-config.json (AdminSettingsAccessApi).
                        path: 'admin-settings-access',
                        element: <RoleProtectedRoute superUserOnly><AdminSettingsAccess /></RoleProtectedRoute>,
                    }
                ],
            },
            {
                path: 'access-mgmt',
                children: [
                    {
                        path: '',
                        element: <Navigate to="org" replace />,
                    },
                    {
                        path: 'org',
                        element: <RoleProtectedRoute pagePath="/admin-settings/access-mgmt/org"><OrgManagement /></RoleProtectedRoute>,
                    },
                    {
                        // Shared "Grant Access" wizard for inviting users at org / game scope,
                        // reached from the unified User Management page. The backend PAP
                        // authorizes the actual grant.
                        path: 'grant-access',
                        element: <RoleProtectedRoute pagePath="/admin-settings/user-mgmt"><GrantAccessWizard /></RoleProtectedRoute>,
                    }
                ],
            },
            {
                path: 'user-mgmt',
                element: <RoleProtectedRoute pagePath="/admin-settings/user-mgmt"><CombinedUserManagement /></RoleProtectedRoute>,
            },
            {
                path: 'game-mgmt',
                children: [
                    {
                        path: '',
                        element: <Navigate to="games" replace />,
                    },
                    {
                        path: 'games',
                        element: <RoleProtectedRoute pagePath="/admin-settings/game-mgmt/games"><GameRegistration /></RoleProtectedRoute>,
                    },
                    // Direct-entry landing for the "Game Workspace" nav
                    // item. Lives at /game-mgmt/workspace so the per-game
                    // tree (/game-mgmt/games/:gameId/*) keeps its own
                    // segment and bookmarks of either form survive.
                    // Gated on the games page permission since picking a
                    // workspace requires being able to see the games
                    // list at all.
                    {
                        path: 'workspace',
                        // Auth-only: the picker is a game selector that only lists workspaces the user
                        // can access and redirects into per-tab-gated routes. No GAME_WORKSPACE gate.
                        element: <GameWorkspacePicker />,
                    },
                    // Tab-scoped landing for the Game Workspace nav children
                    // (Setup / Data Connector / Knowledge). Renders the same
                    // picker, which reads `:tab` and redirects the resolved
                    // game into that workspace tab (see GameWorkspacePicker).
                    {
                        path: 'workspace/:tab',
                        // Auth-only: the picker is a game selector that only lists workspaces the user
                        // can access and redirects into per-tab-gated routes. No GAME_WORKSPACE gate.
                        element: <GameWorkspacePicker />,
                    },
                    // Creating a game moved to the Game space onboarding wizard, reached through the
                    // CreateGameModal on the Games list (and the workspace picker's empty state). This
                    // path stays only to land old bookmarks somewhere sensible — pointing it back at
                    // the retired multi-step form is what the bug report saw. GameRegistrationForm is
                    // still the EDIT form below; only its create mode is unreachable.
                    {
                        path: 'register-game',
                        element: <Navigate to="/admin-settings/game-mgmt/games" replace />,
                    },
                    {
                        path: 'update-game/:gameId',
                        element: <RoleProtectedRoute pagePath="/admin-settings/game-mgmt/games" componentPath={COMPONENTS.GAMES_MUTATE_GAME} minLevel="FULL_ACCESS"><GameRegistrationForm /></RoleProtectedRoute>,
                    },
                    // ── Per-game workspace (Option B IA) ─────────────────
                    // `/game-mgmt/games/:gameClientId/*` is the canonical
                    // home for everything scoped to a single game. URLs
                    // are addressed by the primary build's Client ID
                    // (e.g. `aurora_mobile`) rather than the registry's
                    // Mongo `_id` (e.g. `69b0120a4f03770e5f9b1047`) so
                    // operator-facing URLs read meaningfully and shared
                    // links are decipherable at a glance.
                    //
                    // Multi-build games resolve via any of their builds'
                    // clientIds (the GameWorkspaceLayout fetches via
                    // {@code GET /v1/web/game-mgmt/game-client-id/:id});
                    // the workspace itself remains per-game, surfacing
                    // every build's settings inside.
                    //
                    // Existing per-game URLs (`/game-mgmt/data-connections/:gameId`,
                    // `/game-mgmt/update-game/:gameId`) stay live as
                    // ALIASES at the bottom of this file so bookmarks
                    // and deep links keep working. Phase 4 of the
                    // migration converts those aliases into 301-style
                    // redirects pointing here.
                    {
                        path: 'games/:gameClientId',
                        // Auth-only shell: it just holds the tab strip + Outlet. Each tab route below is
                        // individually gated (GAME_SETUP / DATA_CONNECTIONS / GAME_KNOWLEDGE); a user with
                        // no accessible tab sees no nav entry and hits the tab's own gate if they deep-link.
                        element: <GameWorkspaceLayout />,
                        children: [
                            {
                                // Suspended/sunset scope (game- OR client-level) → the shared "scope
                                // suspended/sunset" lock card, same as the other operational pages
                                // (gameDashboardRoutes). A closed build's Setup/Data Sources/Knowledge
                                // are fully off-limits, not just read-only — reactivation happens from
                                // Game Management instead.
                                element: <RequireActiveScopeOutlet />,
                                children: [
                                    // Default tab = Setup. The bare
                                    // `/games/:gameClientId` URL redirects so the
                                    // URL bar always reflects which tab is active
                                    // (better for shareable links and back-button
                                    // behavior than rendering Setup at the parent
                                    // path).
                                    { path: '',                index: true, element: <Navigate to="setup" replace /> },
                                    { path: 'setup',           element: <RoleProtectedRoute pagePath={PAGES.GAME_SETUP}><GameSetup /></RoleProtectedRoute> },
                                    // Data Sources has its own page permission (DATA_CONNECTIONS) — independently
                                    // grantable from Setup. Its backend (DataConnectionV2Api + WarehouseApi) enforces
                                    // both the DATA_CONNECTIONS component check AND per-build ownsBuild on every
                                    // endpoint, so a build co-owner only ever sees/touches their own build's
                                    // connections + warehouses.
                                    { path: 'data-connections', element: <RoleProtectedRoute pagePath={PAGES.DATA_CONNECTIONS}><DataConnector /></RoleProtectedRoute> },
                                    // 'edit' tab removed — registry editing lives in Game Registration's edit
                                    // flow, not the workspace. Redirect stale bookmarks to Setup instead of 404.
                                    { path: 'edit',            element: <Navigate to="../setup" replace /> },
                                    // Game Knowledge tab (moved from Live Ops). gameId/gameClientId come from
                                    // the workspace Outlet context; gated by the dedicated game-knowledge page.
                                    { path: 'knowledge',         element: <RoleProtectedRoute pagePath={PAGES.GAME_KNOWLEDGE}><GameKnowledge /></RoleProtectedRoute> },
                                    { path: 'knowledge/new',     element: <RoleProtectedRoute pagePath={PAGES.GAME_KNOWLEDGE} componentPath={COMPONENTS.GAME_KNOWLEDGE_CREATE_GAME_KNOWLEDGE} minLevel="FULL_ACCESS"><GameKnowledgeNewPage /></RoleProtectedRoute> },
                                    { path: 'knowledge/extract', element: <RoleProtectedRoute pagePath={PAGES.GAME_KNOWLEDGE} componentPath={COMPONENTS.GAME_KNOWLEDGE_CREATE_GAME_KNOWLEDGE} minLevel="FULL_ACCESS"><GameKnowledgeExtractionPage /></RoleProtectedRoute> },
                                    { path: 'knowledge/drafts',  element: <RoleProtectedRoute pagePath={PAGES.GAME_KNOWLEDGE} componentPath={COMPONENTS.GAME_KNOWLEDGE_MUTATE_GAME_KNOWLEDGE} minLevel="FULL_ACCESS"><GameKnowledgeDraftsPage /></RoleProtectedRoute> },
                                    { path: 'knowledge/upload',  element: <RoleProtectedRoute pagePath={PAGES.GAME_KNOWLEDGE} componentPath={COMPONENTS.GAME_KNOWLEDGE_CREATE_GAME_KNOWLEDGE} minLevel="FULL_ACCESS"><GameKnowledgeBulkUploadPage /></RoleProtectedRoute> },
                                    { path: 'knowledge/add',     element: <Navigate to="../knowledge/upload" replace /> },
                                    // Chunk map for one document, reached by clicking its name in
                                    // the Knowledge list. Identity travels as query params
                                    // (?entity=&name=&slug=) rather than a path segment because
                                    // there is no reliable registry→kb_nodes key yet and file ids
                                    // embed a colon. Its own page permission (VECTOR_INDEX),
                                    // independently grantable from Knowledge: this surface exposes
                                    // stored document text.
                                    { path: 'knowledge/chunk-map', element: <RoleProtectedRoute pagePath={PAGES.VECTOR_INDEX}><ChunkMapPage /></RoleProtectedRoute> },
                                    // 'knowledge' tab is deferred — GameKnowledge
                                    // currently lives under Live Ops; the tab in
                                    // the workspace layout cross-links to that
                                    // URL until Phase 4 moves the page here.
                                ],
                            },
                        ],
                    },
                ],
            },
            // Legacy per-game aliases — same DATA_CONNECTIONS page gate as the workspace tab so the
            // old deep links can't bypass RBAC (they previously rendered ungated).
            {
                path: 'game-mgmt/data-connections/:gameId',
                element: <RoleProtectedRoute pagePath={PAGES.DATA_CONNECTIONS}><DataConnector /></RoleProtectedRoute>,
            },
            {
                path: 'game-mgmt/data-connections/:gameId/add',
                element: <RoleProtectedRoute pagePath={PAGES.DATA_CONNECTIONS} componentPath={COMPONENTS.DATA_CONNECTIONS_CREATE_CONNECTION} minLevel="FULL_ACCESS"><DataConnectorCreatePage /></RoleProtectedRoute>,
            },
            {
                path: 'game-mgmt/data-connections/:gameId/connection/:connectionId/edit-tables',
                element: <RoleProtectedRoute pagePath={PAGES.DATA_CONNECTIONS} componentPath={COMPONENTS.DATA_CONNECTIONS_MUTATE_CONNECTION} minLevel="FULL_ACCESS"><EditTablesPage /></RoleProtectedRoute>,
            },
            {
                path: 'role-mgmt',
                element: <Navigate to="/admin-settings/role-access-mgmt/role" replace />,
            },
            {
                path: 'page-mgmt',
                element: <Navigate to="/admin-settings/role-access-mgmt/page" replace />,
            }
        ],
    },
];

export default settingRoutes;